import { compatWrapper } from '@backstage/core-compat-api';
import type { ExtensionDefinition } from '@backstage/frontend-plugin-api';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import {
  NavContentBlueprint,
  type NavContentNavItem,
} from '@backstage/plugin-app-react';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  getBrandIconConfig,
  toolLinks,
  usefulLinks,
} from '@internal/backstage-plugin-home';
import {
  Settings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import {
  BuildingBank,
  Documents,
  Github,
  Link as LinkIcon,
  Member,
  Menu,
  QuestionMark,
  Search,
  Wrench,
} from '@obosbbl/grunnmuren-icons-react';
import { CustomSidebarLogo } from './CustomSidebarLogo';

function NavItem({ title, icon, href }: NavContentNavItem) {
  return <SidebarItem text={title} icon={() => icon} to={href} />;
}

function sidebarIconFor(icon?: string) {
  const config = getBrandIconConfig(icon);

  return function SidebarBrandIcon() {
    if (!config) {
      return <BuildingBank />;
    }
    if (typeof config === 'string') {
      return <span aria-hidden="true">{config}</span>;
    }
    return (
      <img src={config.src} alt="" aria-hidden="true" width={24} height={24} />
    );
  };
}

const toolSidebarIcons = new Map(
  toolLinks.map(link => [
    link.title,
    link.icon === 'github' ? Github : sidebarIconFor(link.icon),
  ]),
);

function usefulLinkIconFor(icon?: string) {
  switch (icon) {
    case 'faq':
      return QuestionMark;
    case 'cloudid':
      return sidebarIconFor('cloudid');
    case 'cmdb':
      return Documents;
    case 'github':
      return Github;
    default:
      return LinkIcon;
  }
}

const usefulLinkSidebarIcons = new Map(
  usefulLinks.map(link => [link.title, usefulLinkIconFor(link.icon)]),
);

export const SidebarContent: ExtensionDefinition = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const search = navItems.take('page:search');
      const settings = navItems.take('page:user-settings');
      // Hide from sidebar — page kept active for entity card "View Graph" links.
      // catalog-graph doesn't register a NavItemBlueprint, so nav-item:catalog-graph: false has no effect.
      navItems.take('page:catalog-graph');
      const doraLanding = navItems.take('page:dora-metrics/dora-metrics');
      const githubDora = navItems.take('page:dora-metrics/dora-github');
      const spaceliftDora = navItems.take('page:dora-metrics/dora-spacelift');
      const items = navItems.withComponent(NavItem);

      return (
        <Sidebar submenuOptions={{ drawerWidthOpen: 360 }}>
          <CustomSidebarLogo />
          <SidebarGroup label="Search" icon={<Search />} to={search?.href}>
            {compatWrapper(<SidebarSearchModal />)}
          </SidebarGroup>
          <SidebarDivider />

          <SidebarGroup label="Menu" icon={<Menu />}>
            {/* Pinned at the top, rendered with the home plugin's own icon. */}
            {items.take('page:home')}
            {/* Pinned right after Home, rendered with the catalog plugin's own icon. */}
            {items.take('page:catalog')}
            <SidebarItem
              icon={BuildingBank}
              to="/catalog/default/component/blokk-docs/docs"
              text="Blokk"
            />
            {items.take('page:tech-insights')}
            {items.rest({ sortBy: 'title' })}
            {doraLanding && (
              <SidebarItem
                icon={() => doraLanding.icon}
                text={doraLanding.title}
                to={doraLanding.href}
              >
                <SidebarSubmenu title={doraLanding.title}>
                  <SidebarSubmenuItem title="Overview" to={doraLanding.href} />
                  {githubDora && (
                    <SidebarSubmenuItem
                      title={githubDora.title}
                      to={githubDora.href}
                    />
                  )}
                  {spaceliftDora && (
                    <SidebarSubmenuItem
                      title={spaceliftDora.title}
                      to={spaceliftDora.href}
                    />
                  )}
                </SidebarSubmenu>
              </SidebarItem>
            )}
            {compatWrapper(
              <MyGroupsSidebarItem
                singularTitle="My Team"
                pluralTitle="My Teams"
                icon={Member}
              />,
            )}
            <SidebarItem icon={LinkIcon} text="Useful Links">
              <SidebarSubmenu title="Useful Links">
                {usefulLinks.map(link => (
                  <SidebarSubmenuItem
                    key={link.title}
                    icon={usefulLinkSidebarIcons.get(link.title)}
                    to={link.to}
                    title={link.title}
                  />
                ))}
              </SidebarSubmenu>
            </SidebarItem>
            <SidebarItem icon={Wrench} text="Tools">
              <SidebarSubmenu title="Tools">
                {toolLinks.map(link => (
                  <SidebarSubmenuItem
                    key={link.title}
                    icon={toolSidebarIcons.get(link.title)}
                    to={link.to}
                    title={link.title}
                  />
                ))}
              </SidebarSubmenu>
            </SidebarItem>
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          {settings && (
            <SidebarGroup
              label={settings.title}
              icon={<UserSettingsSignInAvatar />}
              to={settings.href}
            >
              {compatWrapper(<Settings />)}
            </SidebarGroup>
          )}
        </Sidebar>
      );
    },
  },
});
